import createContext from "./createContext";
import {doc, setDoc, updateDoc} from "firebase/firestore";
import {
    PROJECTS_COLLECTION,
    PROJECT_VERSION_COLLECTION
} from "../db/collections";
import {db} from "../config/firebase";
import {toast} from "react-toastify";
import {v4 as uuidv4} from 'uuid';
import {FlowEntity, NodeEntity, NodeType, UserDetailsEntity} from "../model/ModelData";
import axios from "axios";
import {MCPServersRepository} from "../repositories/MCPServersRepository";
import {KnowledgeDatabaseRepository} from "../repositories/KnowledgeDatabaseRepository";

export const ProjectRoles = {
    ADMIN: "ADMIN",
    MANAGER: "MANAGER",
    AGENT: "AGENT",
    ANNOTATOR: "ANNOTATOR"
}

const appInitialState = {
    userDetails: {...UserDetailsEntity},
    projects: [],
    mcpServers: [],
    mcpLoading: false,
    kbTopics: [],
    kbTopicsLoading: false,
    kbWorkspace: {}
}

const appReducer = (state, action) => {
    switch (action.type) {
        case 'update_flow':
            return {...state, flow: action.payload}
        case 'update_flows':
            return {...state, flows: action.payload}
        case 'update_projects':
            return {...state, projects: action.payload}
        case 'update_mcp_servers':
            return {...state, mcpServers: action.payload}
        case 'update_mcp_loading':
            return {...state, mcpLoading: action.payload}
        case 'update_single_mcp_server':
            return {
                ...state,
                mcpServers: state.mcpServers.map(s =>
                    s.id === action.payload.id ? action.payload : s
                )
            }
        case 'remove_mcp_server':
            return {
                ...state,
                mcpServers: state.mcpServers.filter(s => s.id !== action.payload)
            }
        case 'add_mcp_server':
            return {...state, mcpServers: [...state.mcpServers, action.payload]}
        case 'update_kb_topics':
            return {...state, kbTopics: action.payload}
        case 'update_kb_loading':
            return {...state, kbTopicsLoading: action.payload}
        case 'update_kb_workspace':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: {
                        ...state.kbWorkspace[action.topicId],
                        ...action.payload,
                    }
                }
            }
        case 'update_kb_documents':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: {
                        ...state.kbWorkspace[action.topicId],
                        documents: action.payload,
                    }
                }
            }
        case 'remove_kb_document':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: {
                        ...state.kbWorkspace[action.topicId],
                        documents: (state.kbWorkspace[action.topicId]?.documents ?? [])
                            .filter(d => d.id !== action.docId),
                    }
                }
            }
        case 'add_kb_query':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: {
                        ...state.kbWorkspace[action.topicId],
                        queries: [action.payload, ...(state.kbWorkspace[action.topicId]?.queries ?? [])],
                    }
                }
            }
        case 'update_kb_graph':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: {
                        ...state.kbWorkspace[action.topicId],
                        graph: action.payload,
                    }
                }
            }
        case 'clear_kb_workspace':
            return {
                ...state,
                kbWorkspace: {
                    ...state.kbWorkspace,
                    [action.topicId]: undefined,
                }
            }
        default:
            return state;
    }
}

// these functions will act as repositories, here we will make calls to the database and be able to reuse them in different components
const createProject = (dispatch, authContext) => {
    return async (data) => {
        try {
            const uuid = uuidv4();
            if (!data?.id) {
                data.id = uuid;
            }
            data.userUid = authContext.currentUser.username;
            data.team = [authContext.currentUser.username];
            //
            const flow = {...FlowEntity, id: uuidv4()};
            const textNode = {
                ...NodeEntity,
                id: uuidv4(),
                name: "Welcome",
                type: NodeType.TEXT,
                coords: {x: 150, y: 0},
                data: {
                    greet: ["Како може да ви помогнам"]
                }
            }
            const startNode = {
                ...NodeEntity,
                id: uuidv4(),
                name: "Start",
                type: NodeType.START,
                data: {
                    greet: ["Добредојде во контакт центарот на А1", "Добредојде"]
                },
                routes: {
                    static: {
                        next: {
                            id: uuidv4(),
                            type: "next",
                            target: textNode.id,
                            data: {}
                        },
                    }
                }
            }
            flow.nodes = [startNode, textNode];
            data.flows = [flow];
            data.entryNode = startNode?.id;

            console.log(data);
            await setDoc(doc(db, PROJECTS_COLLECTION, data?.id), data);
            await getUserProjects(dispatch, authContext)();
            toast.success("Successfully created project.")
        } catch (err) {
            toast.error(err);
        }
    }
}

const publishVersion = (dispatch, authContext) => {
    return async (project) => {
        try {
            if (!project) {
                throw Error("Project not found");
            }
            const newProject = {...project};
            const newVersion = {...newProject};
            newVersion.id = uuidv4();
            newVersion.projectId = newProject.id;
            newVersion.createdAt = new Date().getTime();
            newVersion.createdBy = authContext.currentUser.username;
            if (newVersion.versions) {
                delete newVersion.versions;
            }
            await setDoc(doc(db, PROJECT_VERSION_COLLECTION, newVersion?.id), newVersion);

            if (!newProject.versions) {
                newProject.versions = [];
            }
            newProject.versions.push(
                {
                    id: newVersion.id,
                    createdAt: newVersion.createdAt,
                    version: `V${(newProject.versions.length + 1).toString().padStart(2, '0')}`
                }
            );
            await setDoc(doc(db, PROJECTS_COLLECTION, newProject?.id), newProject);

            // const response = await axios.get(`https://intents.vb.openbrain.io/publish?project_id=${newProject.id}&export_url=${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${newProject.id}`)
            // if (response.status !== 200) {
            //   toast.error("Error! Please try again.");
            //   throw Error("Publish version failed!");
            // }

            await getUserProjects(dispatch, authContext)();
            toast.success("Successfully published new version.")
        } catch (err) {
            toast.error(err);
        }
    }
}

const updateProject = (dispatch, authContext) => {
    return async (data) => {
        try {
            const uuid = uuidv4();
            if (!data?.id) {
                data.id = uuid;
            }
            if (!data.userUid) {
                data.userUid = authContext.currentUser.username;
            }
            data.updatedAt = new Date().getTime();
            await updateDoc(doc(db, PROJECTS_COLLECTION, data?.id), data);
        } catch (err) {
            toast.error(err);
            console.error(err);
        }
    }
}

const getUserProjects = (dispatch, authContext) => {
    return async (props) => {
        const {setLoading} = props || {}
        if (setLoading) {
            setLoading(true)
        }
        try {
            if (authContext?.currentUser && authContext?.isAuth) {
                const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/getProjects`
                const organizations = authContext?.organizations?.map(org => org?.id) || [];
                const response = await axios.post(url, {
                    user: authContext.currentUser.username,
                    organizations: organizations,
                    roles: authContext.currentUser.roles
                })
                const items = response?.data?.projects?.sort((p1, p2) => (p1.createdAt < p2.createdAt) ? 1 : (p1.createdAt > p2.createdAt) ? -1 : 0) || [];
                console.log(items);
                // console.log(items);
                dispatch({type: "update_projects", payload: items});
            }
        } catch
            (err) {
            toast.error(err.message);
        } finally {
            if (setLoading) {
                setLoading(false)
            }
        }
    }
}

const fetchServerDetails = async (server) => {
    const [toolsRes, promptsRes] = await Promise.allSettled([
        MCPServersRepository.getTools({server_id: server.id}),
        MCPServersRepository.getPrompts({server_id: server.id}),
    ]);
    return {
        ...server,
        tools: toolsRes.status === "fulfilled" ? (toolsRes.value.data || []) : [],
        prompts: promptsRes.status === "fulfilled" ? (promptsRes.value.data || {}) : {},
    };
};

const getMCPServers = (dispatch, authContext) => {
    return async (projectId) => {
        dispatch({type: "update_mcp_loading", payload: true});
        try {
            const res = await MCPServersRepository.getAll(projectId);
            const baseServers = res?.data || [];
            const enriched = await Promise.all(baseServers.map(fetchServerDetails));
            dispatch({type: "update_mcp_servers", payload: enriched});
        } catch (err) {
            toast.error(err.message || "Failed to load MCP servers");
        } finally {
            dispatch({type: "update_mcp_loading", payload: false});
        }
    }
}

const getAllServersWithDetails = (dispatch, authContext) => {
    return async (projectId) => {
        try {
            dispatch({type: "update_mcp_loading", payload: true});
            const res = await MCPServersRepository.getAllServersWithDetails(projectId);
            dispatch({type: "update_mcp_servers", payload: res.data});
            return res.data;
        } catch (err) {
            toast.error(err.message || "Failed to load server details");
            return [];
        } finally {
            dispatch({type: "update_mcp_loading", payload: false});
        }
    };
};

const getServerDetails = (dispatch, authContext) => {
    return async (serverId) => {
        try {
            const res = await MCPServersRepository.getServerWithDetails(serverId);
            return res.data;
        } catch (err) {
            toast.error(err.message || "Failed to load server details");
            return null;
        }
    };
};
const getToolDetails = (dispatch, authContext) => {
    return async (serverId, toolName) => {
        try {
            console.log("getToolDetails sending:", {server_id: serverId, tool_name: toolName});
            const res = await MCPServersRepository.getToolDetails({
                server_id: serverId,
                tool_name: toolName,
            });
            return res.data;
        } catch (err) {
            toast.error(err.message || "Failed to load tool details");
            return null;
        }
    };
};

const reloadMCPServer = (dispatch, authContext) => {
    return async (server) => {
        try {
            await MCPServersRepository.reloadServer(server.id);
            const enriched = await fetchServerDetails(server);
            dispatch({type: "update_single_mcp_server", payload: enriched});
            toast.success(`Server ${server.name} reloaded`);
        } catch (err) {
            toast.error(err.message || "Failed to reload server");
        }
    }
}

const updateMCPServer = (dispatch, authContext) => {
    return async (id, data) => {
        try {
            const res = await MCPServersRepository.edit(id, data);
            const enriched = await fetchServerDetails(res.data);
            dispatch({type: "update_single_mcp_server", payload: enriched});
            toast.success("Server updated");
            return enriched;
        } catch (err) {
            toast.error(err.message || "Failed to update server");
            throw err;
        }
    }
}

const createMCPServer = (dispatch, authContext) => {
    return async (data) => {
        try {
            const res = await MCPServersRepository.create(data);
            const enriched = await fetchServerDetails(res.data);
            dispatch({type: "add_mcp_server", payload: enriched});
            return enriched;
        } catch (err) {
            toast.error(err.message || "Failed to create server");
        }
    }
}

const deleteMCPServer = (dispatch, authContext) => {
    return async (id) => {
        try {
            await MCPServersRepository.delete(id);
            dispatch({type: "remove_mcp_server", payload: id});
            toast.success("Server deleted");
        } catch (err) {
            toast.error(err.message || "Failed to delete server");
        }
    }
}

const callMCPTool = (dispatch, authContext) => {
    return async (data) => {
        try {
            const res = await MCPServersRepository.callTool(data);
            return res.data;
        } catch (err) {
            toast.error(err.message || "Tool call failed");
            throw err;
        }
    }
}
const testMCPConnection = (dispatch, authContext) => {
    return async (payload) => {
        try {
            const {data} = await MCPServersRepository.testConnection(payload);
            return data;
        } catch (e) {
            return {ok: false, message: "Request failed — check the backend."};
        }
    };
};

const deleteInactiveTool = (dispatch, authContext) => {
    return async (toolId) => {
        try {
            const {data} = await MCPServersRepository.deleteInactiveTool(toolId);
            return data;
        } catch (e) {
            console.error("Failed to delete tool:", e);
            return null;
        }
    };
};

const getMCPServerTools = (dispatch, authContext) => {
    return async (server) => {
        try {
            const res = await MCPServersRepository.getTools({
                server_id: server.id,
                endpoint: server.endpoint
            });
            return res.data.tools;
        } catch (err) {
            console.error("Failed to fetch tools for", server.name, err);
            return [];
        }
    }
}

const editServerToken = (dispatch, authContext) => {
    return async (serverId, token) => {
        try {
            const res = await MCPServersRepository.editServerToken(serverId, {
                auth_token: token
            });
            return res.data;
        } catch (err) {
            console.error("Failed to update token", err);
            throw err;
        }
    }
}

const getAllKbTopics = (dispatch, authContext) => {
    return async (organizationId) => {
        try {
            dispatch({type: "update_kb_loading", payload: true});
            const res = await KnowledgeDatabaseRepository.listTopics(organizationId);
            dispatch({type: "update_kb_topics", payload: res});
            return res;
        } catch (err) {
            toast.error(err.message || "Failed to load topics");
            return [];
        } finally {
            dispatch({type: "update_kb_loading", payload: false});
        }
    };
};

const getKbQueries = (dispatch, authContext) => {
    return async (topicId) => {
        try {
            const res = await KnowledgeDatabaseRepository.listQueries(topicId);
            dispatch({type: "update_kb_queries", topicId, payload: res});
            return res;
        } catch (err) {
            toast.error(err.message || "Failed to load queries");
            return [];
        }
    };
};

const executeKbQuery = (dispatch, authContext) => {
    return async (topicId, payload) => {
        try {
            const result = await KnowledgeDatabaseRepository.executeQuery(topicId, payload);
            dispatch({type: "add_kb_query", topicId, payload: result});
            return result;
        } catch (err) {
            toast.error(err.message || "Query failed");
            throw err;
        }
    };
};

const loadKbWorkspace = (dispatch, authContext) => {
    return async (topicId, graphOptions = {}) => {
        const [topicRes, docsRes, queriesRes] = await Promise.allSettled([
            KnowledgeDatabaseRepository.getById(topicId),
            KnowledgeDatabaseRepository.listDocuments(topicId),
            KnowledgeDatabaseRepository.listQueries(topicId),
        ]);

        const topic = topicRes.status === "fulfilled" ? topicRes.value : null;

        let label = graphOptions.label;
        let graph = null;
        if (!label) {
            try {
                const popular = await KnowledgeDatabaseRepository.listPopularLabels(topicId, 12);
                label = popular[0] ?? topic?.name;
            } catch {
                label = topic?.name;
            }
        }

        if (label) {
            try {
                graph = await KnowledgeDatabaseRepository.fetch(topicId, {
                    label,
                    maxDepth: graphOptions.maxDepth ?? 2,
                    maxNodes: graphOptions.maxNodes ?? 100,
                });
            } catch (err) {
                console.error("Failed to load graph:", err);
            }
        }

        dispatch({
            type: "update_kb_workspace",
            topicId,
            payload: {
                topic,
                documents: docsRes.status === "fulfilled" ? docsRes.value : [],
                queries: queriesRes.status === "fulfilled" ? queriesRes.value : [],
                graph,
                activeGraphLabel: label ?? null,
            }
        });

        if (topicRes.status === "rejected") toast.error("Failed to load topic");
    };
};
const refreshKbTopic = (dispatch, authContext) => {
    return async (topicId) => {
        try {
            const topic = await KnowledgeDatabaseRepository.getById(topicId);
            dispatch({type: "update_kb_workspace", topicId, payload: {topic}});
            return topic;
        } catch {
            toast.error("Failed to reload topic");
        }
    };
};

const refreshKbDocuments = (dispatch, authContext) => {
    return async (topicId) => {
        try {
            const documents = await KnowledgeDatabaseRepository.listDocuments(topicId);
            dispatch({type: "update_kb_documents", topicId, payload: documents});
            return documents;
        } catch {
            toast.error("Failed to load documents");
        }
    };
};

const deleteKbDocument = (dispatch, authContext) => {
    return async (topicId, docId) => {
        try {
            await KnowledgeDatabaseRepository.deleteDocument(docId);
            dispatch({type: "remove_kb_document", topicId, docId});
            toast.success("Document removed");
        } catch {
            toast.error("Failed to delete document");
            throw new Error("delete failed");
        }
    };
};


const pingKbTopic = (dispatch, authContext) => {
    return async (topicId) => {
        await KnowledgeDatabaseRepository.checkHealth(topicId);
        const topic = await KnowledgeDatabaseRepository.getById(topicId);
        dispatch({type: "update_kb_workspace", topicId, payload: {topic}});
        return topic;
    };
};
export const {Provider, Context} = createContext(
    appReducer,
    {
        getUserProjects,
        getToolDetails,
        updateProject,
        createProject,
        publishVersion,
        getMCPServers,
        reloadMCPServer,
        createMCPServer,
        deleteMCPServer,
        callMCPTool,
        getMCPServerTools,
        updateMCPServer,
        testMCPConnection,
        editServerToken,
        getServerDetails,
        getAllServersWithDetails,
        deleteInactiveTool,
        getAllKbTopics,
        getKbQueries,
        executeKbQuery,
        loadKbWorkspace,
        deleteKbDocument,
        refreshKbTopic
    },
    {...appInitialState}
)